import { types as t, Instance } from 'mobx-state-tree';
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';
import { ScheduledConfiguration, OnceConfiguration } from '~/mst/models/report/configuration/base';
import AQConfiguration from '~/mst/models/report/configuration/aq_report';
import PerimeterMonitor from '~/mst/models/report/configuration/perimeter_monitor_report';
import Cms2567Report from '~/mst/models/report/configuration/cms_2567_report';
import { ReportTypes } from '~/mst/models/report/types';

function getReportModel(type, base, runType) {
  return t.compose(
    `${upperFirst(camelCase(type))}${upperFirst(runType)}`,
    base,
    runType === 'scheduled' ? ScheduledConfiguration : OnceConfiguration
  );
}

const ReportConfiguration = t.union({
  dispatcher(snapshot) {
    const reportType = snapshot?.type;
    const reportRunType = snapshot?.run_type;
    switch (reportType) {
      case ReportTypes.air_quality:
        return getReportModel(reportType, AQConfiguration, reportRunType);
      case ReportTypes.perimeter_monitor:
        return getReportModel(reportType, PerimeterMonitor, reportRunType);
      case ReportTypes.aethair_cms_2567:
        return getReportModel(reportType, Cms2567Report, reportRunType);
      default:
        return getReportModel(reportType, AQConfiguration, reportRunType);
    }
  }
});

export interface ICondition extends Instance<typeof ReportConfiguration> {}

export default t.snapshotProcessor(ReportConfiguration, {
  preProcessor(values) {
    const result = { ...values };
    if (values.standard === 'aqi') {
      result.standard = 'aethair_blue';
    }
    if (values.type === 'air_quality_crp') {
      result.type = 'air_quality';
    }
    if (values.type === 'aqi_basic' || values.type === 'aqi_extended') {
      result.type = ReportTypes.air_quality;
      if (values.type === 'aqi_extended') {
        result.view = 'extended';
      } else {
        result.view = 'basic';
      }
    }
    if (result.type === ReportTypes.perimeter_monitor) {
      if (result.wind_speed != null && Array.isArray(result.wind_speed)) {
        result.wind_speed = result.wind_speed[0];
      }
      if (result.wind_direction != null && Array.isArray(result.wind_direction)) {
        result.wind_direction = result.wind_direction[0];
      }
    }
    return result;
  },
  postProcessor(sn) {
    const result = { ...sn };
    if (sn.standard === 'aqi') {
      result.standard = 'aethair_blue';
    }
    if (sn.type === 'aqi_basic' || sn.type === 'aqi_extended') {
      result.type = ReportTypes.air_quality;
      if (sn.type === 'aqi_extended') {
        result.view = 'extended';
      } else {
        result.view = 'basic';
      }
    }
    if (sn.type === ReportTypes.perimeter_monitor) {
      if (!sn.wind_detection) {
        result.wind_detection = 'auto';
        result.parameters = result.parameters.map(({ expr, ...rest }: { expr?: string; [key: string]: unknown }) => ({
          ...rest,
          expr: expr || 'gt'
        }));
      }
      if (sn.wind_detection === 'manual' && result.wind_manual_node_ids == null) {
        result.wind_manual_node_ids = result.node_ids;
        result.node_ids = [];
      }
      if (sn.wind_speed != null && Array.isArray(sn.wind_speed)) {
        result.wind_speed = sn.wind_speed[0];
      }
      if (sn.wind_direction != null && Array.isArray(sn.wind_direction)) {
        result.wind_direction = sn.wind_direction[0];
      }
    }
    return result;
  }
});
